'use client';

import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
	className?: string;
}

/**
 * Light/dark switch. Mounted on nearly every SSR'd page, so its markup has to be
 * the same on the server and on the first client render.
 *
 * next-themes cannot help with that: `resolvedTheme` is `undefined` on the
 * server but already resolved in the client hook's `useState` initializer, so
 * anything derived from it — which icon, the aria-label, a `disabled` flag —
 * disagrees across the hydration boundary. For a dark-mode user the two trees
 * differed by element type (Sun vs Moon), which React 19 reports as a
 * recoverable hydration error and repairs by re-rendering the subtree.
 *
 * So nothing about the markup reads theme state. Both icons are always in the
 * DOM and CSS picks one off the `.dark` class next-themes puts on <html>
 * (`attribute="class"` in providers.tsx), which is applied by its blocking
 * pre-hydration script — before first paint, and identically on both sides
 * because it is not React state. The label stays constant for the same reason.
 * `resolvedTheme` is still read in the click handler, where it is runtime-only
 * and hydration cannot see it.
 *
 * `ThemeSegment` needs the live value to mark a segment active and pays for it
 * with a `useSyncExternalStore` mounted guard; this control does not need it.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
	const { resolvedTheme, setTheme } = useTheme();

	return (
		<Button
			type="button"
			variant="ghost"
			size="icon"
			onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
			className={cn('rounded-full text-muted-foreground hover:text-foreground', className)}
			aria-label="Toggle theme"
		>
			{/* Dark is showing → offer the sun; light is showing → offer the moon. */}
			<Sun className="hidden dark:block" aria-hidden="true" />
			<Moon className="block dark:hidden" aria-hidden="true" />
		</Button>
	);
}
